<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading', 'auth'],
  data() {
    return {
      loading: true,
      tableColumns: [
        { key: 'employee', label: 'Employee' },
        { key: 'date', label: 'Date' },
        { key: 'requested', label: 'Requested In / Out' },
        { key: 'reason', label: 'Reason' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: 'Actions', width: '80px' },
      ],
      records: [],
      employees: [],
      filters: { employee_id: '', status: '' },
      statusOptions: ['pending', 'approved', 'rejected'],
      pagination: { current_page: 1, per_page: 50, last_page: 1, total: 0, links: [] },

      // reject modal
      rejectItem: null,
      rejectReason: '',
    };
  },
  computed: {
    isAdmin() { return this.auth?.type === 'ADMIN'; },
    // employee ko Employee + Actions column mat dikhao
    visibleColumns() {
      return this.isAdmin
        ? this.tableColumns
        : this.tableColumns.filter(c => c.key !== 'employee' && c.key !== 'actions');
    },
  },
  mounted() {
    if (this.isAdmin) this.fetchEmployees();
    this.fetchRecords();
  },
  methods: {
    async fetchEmployees() {
      try {
        const res = await axios.get('/hrms/employees/dropdown');
        this.employees = res.data.data || [];
      } catch (error) {
        console.log(error.message);
      }
    },

    async fetchRecords(page = 1) {
      this.loading = true;
      try {
        const endpoint = this.isAdmin
          ? '/hrms/attendances/regularization-requests'      // admin → sabki
          : '/hrms/attendances/my-regularization-requests';  // employee → apni

        const res = await axios.get(endpoint, {
          params: {
            page,
            employee_id: this.isAdmin ? (this.filters.employee_id || undefined) : undefined,
            status: this.filters.status || undefined,
          },
        });

        const d = res.data.data;
        // could be paginated (d.data) or flat array
        if (Array.isArray(d)) {
          this.records = d;
          this.pagination = { current_page: 1, per_page: d.length, last_page: 1, total: d.length, links: [] };
        } else {
          this.records = d.data;
          this.pagination = {
            current_page: d.current_page, per_page: d.per_page,
            last_page: d.last_page, total: d.total, links: d.links,
          };
        }
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    applyFilters() { this.fetchRecords(1); },
    clearFilters() {
      this.filters = { employee_id: '', status: '' };
      this.fetchRecords(1);
    },

    employeeLabel(e) {
      return e.full_name || `${e.first_name || ''} ${e.last_name || ''}`.trim();
    },

    fmtTime(val) {
      if (!val) return '-';
      // could be "10:00:00" or ISO
      if (/^\d{2}:\d{2}/.test(val)) return val.slice(0, 5);
      const d = new Date(val);
      return isNaN(d.getTime()) ? '-' : d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
    },

    statusClass(s) {
      const map = { pending: 'reg-st reg-pending', approved: 'reg-st reg-approved', rejected: 'reg-st reg-rejected' };
      return map[s] || 'reg-st reg-pending';
    },

    openCreate() {
      this.$refs.createModal.open();
    //   this.$openModal('hrm_regularization_create_modal');
    },

    async approve(row) {
      this.setLoading(true);
      try {
        const res = await axios.put(`/hrms/attendances/regularization/${row.id}`, { action: 'approve' });
        this.$swalToast(res.data.message || 'Approved.');
        this.fetchRecords(this.pagination.current_page);
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },

    openReject(row) {
      this.rejectItem = row;
      this.rejectReason = '';
      this.$openModal('hrm_regularization_reject_modal');
    },

    async confirmReject() {
      if (!this.rejectReason.trim()) {
        this.$swalToast('Please enter a rejection reason.', 'error');
        return;
      }
      this.setLoading(true);
      try {
        const res = await axios.put(`/hrms/attendances/regularization/${this.rejectItem.id}`, {
          action: 'reject',
          rejection_reason: this.rejectReason,
        });
        this.$swalToast(res.data.message || 'Rejected.');
        this.$closeModal('hrm_regularization_reject_modal');
        this.fetchRecords(this.pagination.current_page);
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <div class="d-flex align-items-center gap-2 reg-title-row">
          <a href="javascript:void(0);" class="btn btn-icon btn-light flex-shrink-0" @click="$router.push('/hrms/attendances')"><i class="ti ti-arrow-left"></i></a>
          <h4 class="page-title mb-0">{{ isAdmin ? 'Regularization Requests' : 'My Regularizations' }}</h4>
        </div>
        <div class="reg-new-btn-wrap">
          <a href="javascript:void(0);" class="btn btn-primary d-inline-flex align-items-center justify-content-center" @click="openCreate">
            <i class="ti ti-circle-plus me-2"></i>New Request
          </a>
        </div>
      </div>

      <!-- Filters -->
      <div class="card border-0 shadow-sm mb-3" style="border-radius:14px;">
        <div class="card-body d-flex align-items-end flex-wrap gap-2 py-3 px-4 reg-filters">
          <div v-if="isAdmin" class="reg-filter-item">
            <label class="reg-flabel">Employee</label>
            <select class="form-select form-select-sm" style="min-width:160px;" v-model="filters.employee_id">
              <option value="">All Employees</option>
              <option v-for="e in employees" :key="e.id" :value="e.id">{{ employeeLabel(e) }}</option>
            </select>
          </div>
          <div class="reg-filter-item">
            <label class="reg-flabel">Status</label>
            <select class="form-select form-select-sm" style="min-width:130px;" v-model="filters.status">
              <option value="">All Status</option>
              <option v-for="s in statusOptions" :key="s" :value="s">{{ s.charAt(0).toUpperCase() + s.slice(1) }}</option>
            </select>
          </div>
          <div class="reg-filter-actions">
            <button class="btn btn-sm btn-primary" @click="applyFilters"><i class="ti ti-filter me-1"></i>Apply</button>
            <button class="btn btn-sm btn-light" @click="clearFilters"><i class="ti ti-x me-1"></i>Clear</button>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="card border-0 shadow-sm mb-0" style="border-radius:14px;">
        <div class="card-header bg-white border-bottom py-3 px-4">
          <h5 class="mb-0 fw-bold" style="font-size:14px; color:#0f172a;">
            Requests
            <span style="font-size:11px; color:#94a3b8; font-weight:500; margin-left:6px;">({{ pagination.total }})</span>
          </h5>
        </div>
        <div class="card-body px-3 pt-2 pb-3" style="background:#f1f5f9;">
          <div class="reg-table-scroll">
          <app-table
            :columns="visibleColumns"
            :rows="records"
            :pagination="pagination"
            :loading="loading"
            empty-text="No regularization requests."
            empty-icon="ti ti-clock-edit"
            @page-change="fetchRecords"
          >
            <template #employee="{ row }">
              <div class="d-flex flex-column">
                <span style="font-size:12.5px; font-weight:600; color:#1e293b;">{{ row.employee?.full_name || '-' }}</span>
                <span v-if="row.employee?.code" class="reg-code">{{ row.employee.code }}</span>
              </div>
            </template>

            <template #date="{ row }">
              <span style="font-size:12px; color:#475569;">{{ $formatDate(row.date) }}</span>
            </template>

            <template #requested="{ row }">
              <span style="font-size:11.5px; color:#16a34a; font-weight:600;">{{ fmtTime(row.requested_punch_in) }}</span>
              <span style="font-size:11px; color:#cbd5e1;"> → </span>
              <span style="font-size:11.5px; color:#dc2626; font-weight:600;">{{ fmtTime(row.requested_punch_out) }}</span>
            </template>

            <template #reason="{ row }">
              <span style="font-size:11.5px; color:#475569;" :title="row.reason">{{ row.reason || '-' }}</span>
            </template>

            <template #status="{ row }">
              <span :class="statusClass(row.status)">{{ row.status }}</span>
              <div v-if="row.status === 'rejected' && row.rejection_reason" style="font-size:10px; color:#dc2626; margin-top:2px;" :title="row.rejection_reason">
                {{ row.rejection_reason }}
              </div>
            </template>

            <template #actions="{ row }">
              <div v-if="row.status === 'pending'" class="d-flex gap-1">
                <button class="btn btn-sm btn-success" @click="approve(row)" title="Approve"><i class="ti ti-check"></i></button>
                <button class="btn btn-sm btn-danger" @click="openReject(row)" title="Reject"><i class="ti ti-x"></i></button>
              </div>
              <span v-else style="font-size:10px; color:#cbd5e1;">—</span>
            </template>
          </app-table>
          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>

  <!-- Reject Modal -->
  <div class="modal fade" id="hrm_regularization_reject_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">Reject Request</h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal"><i class="ti ti-x"></i></button>
        </div>
        <div class="modal-body">
          <label class="form-label">Rejection Reason <span class="text-danger">*</span></label>
          <textarea class="form-control" rows="3" v-model="rejectReason" placeholder="Why is this being rejected?"></textarea>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">Cancel</button>
          <button type="button" class="btn btn-danger" @click="confirmReject">Reject</button>
        </div>
      </div>
    </div>
  </div>

  <hrm-regularization-create-modal ref="createModal" @updated="fetchRecords" />
</template>

<style scoped>
.reg-flabel { font-size: 10.5px; font-weight: 600; color: #64748b; display: block; margin-bottom: 3px; }
.reg-code { font-size: 10px; font-weight: 700; letter-spacing: .4px; color: #94a3b8; margin-top: 1px; }
.reg-st { display: inline-block; font-size: 10px; font-weight: 600; padding: 3px 10px; border-radius: 20px; text-transform: capitalize; }
.reg-pending { background: #fef3c7; color: #b45309; }
.reg-approved { background: #dcfce7; color: #16a34a; }
.reg-rejected { background: #fee2e2; color: #dc2626; }

/* Table horizontal scroll on small screens */
.reg-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.reg-table-scroll :deep(table) { min-width: 650px; }

/* ===== Responsive fixes ===== */
@media (max-width: 767px) {
  .page-breadcrumb { flex-direction: column; align-items: stretch !important; gap: 10px; }
  .reg-title-row { min-width: 0; }
  .reg-title-row .page-title { font-size: 15px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  .reg-new-btn-wrap { width: 100%; }
  .reg-new-btn-wrap .btn { width: 100%; font-size: 13px; padding: 9px 14px; }

  .reg-filters { flex-direction: column; align-items: stretch !important; }
  .reg-filter-item { width: 100%; }
  .reg-filter-item .form-select { width: 100% !important; min-width: 100% !important; }
  .reg-filter-actions { width: 100%; display: flex; gap: 8px; }
  .reg-filter-actions .btn { flex: 1; }
}
</style>